import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  Stack,
  TextField,
  MenuItem,
  Button,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  Select,
  InputLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Avatar,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Alert,
} from "@mui/material";
import { Formik } from "formik";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { getDSOKProducts } from "app/services/api/management";

function GiftProductPicker({ t, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [products, setProducts] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const { getAuthUser } = useJumboAuth();
  const { customer } = getAuthUser();
  const defaultLanguage =
    customer?.dsokSettings?.customer?.defaultLanguage || "en";

  const imageUrl = (p) =>
    p?.image || p?.customProductImageUrl || p?.imageUrl || "";
  const resolveText = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") {
      return (
        val[defaultLanguage] ||
        val.en ||
        val.sk ||
        val.cs ||
        val.de ||
        Object.values(val)[0] ||
        ""
      );
    }
    return String(val);
  };
  const titleOf = (p) => resolveText(p?.productName) || p?._id;

  const filtered = React.useMemo(() => {
    const s = (search || "").toLowerCase();
    if (!s) return products;
    return (products || []).filter((p) =>
      String(titleOf(p) || "")
        .toLowerCase()
        .includes(s),
    );
  }, [products, search]);

  const ensureProductsLoaded = async () => {
    if (products && products.length) return;
    setLoading(true);
    try {
      const list = await getDSOKProducts();
      setProducts(Array.isArray(list) ? list : []);
    } finally {
      setLoading(false);
    }
  };

  const selectedId = React.useMemo(
    () => (value && typeof value === "object" ? value.id : value),
    [value],
  );
  const selectedProduct = React.useMemo(() => {
    return (products || []).find((p) => String(p._id) === String(selectedId));
  }, [products, selectedId]);

  React.useEffect(() => {
    if (selectedId && (!products || products.length === 0)) {
      ensureProductsLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const handleOpen = async () => {
    setOpen(true);
    await ensureProductsLoaded();
  };

  return (
    <>
      <Grid item xs={12} md={6} lg={6}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("pages.dsokCartRules.giftProductId", {
            defaultValue: "Gift product ID",
          })}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            src={
              (value && typeof value === "object" && value.image) ||
              imageUrl(selectedProduct)
            }
            alt={titleOf(selectedProduct)}
            sx={{ width: 56, height: 56, cursor: "pointer" }}
            onClick={handleOpen}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" noWrap>
              {(value &&
                typeof value === "object" &&
                resolveText(value.productName)) ||
                titleOf(selectedProduct) ||
                t("common.select", { defaultValue: "Select" })}
            </Typography>
            {selectedId ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {String(selectedId)}
              </Typography>
            ) : null}
          </Box>
          <Button variant="outlined" onClick={handleOpen}>
            {t("common.select", { defaultValue: "Select" })}
          </Button>
          {selectedId ? (
            <Button
              variant="text"
              color="error"
              onClick={() => onChange({ id: "", productName: {}, image: "" })}
            >
              ×
            </Button>
          ) : null}
        </Stack>
      </Grid>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {t("pages.dsokCartRules.giftProductId", {
            defaultValue: "Gift product ID",
          })}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            placeholder={t("pages.presentation.searchPresentation", {
              defaultValue: "Search...",
            })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          {loading ? (
            <CircularProgress />
          ) : (
            <List dense>
              {(filtered || []).map((p) => (
                <ListItemButton
                  key={p._id}
                  onClick={() => {
                    onChange({
                      id: p._id,
                      productName: p?.productName || {},
                      image: imageUrl(p),
                    });
                    setOpen(false);
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={imageUrl(p)} alt={titleOf(p)} />
                  </ListItemAvatar>
                  <ListItemText primary={titleOf(p)} secondary={p._id} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>
            {t("buttons.cancel", { defaultValue: "Cancel" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const DAYS_OF_WEEK = [
  { key: "MON", labelKey: "days.monday", fallback: "Monday" },
  { key: "TUE", labelKey: "days.tuesday", fallback: "Tuesday" },
  { key: "WED", labelKey: "days.wednesday", fallback: "Wednesday" },
  { key: "THU", labelKey: "days.thursday", fallback: "Thursday" },
  { key: "FRI", labelKey: "days.friday", fallback: "Friday" },
  { key: "SAT", labelKey: "days.saturday", fallback: "Saturday" },
  { key: "SUN", labelKey: "days.sunday", fallback: "Sunday" },
];

const DISCOUNT_TYPES = [
  {
    value: "percent",
    labelKey: "pages.dsokCartRules.discountType.percent",
    fallback: "Percent",
  },
  {
    value: "amount",
    labelKey: "pages.dsokCartRules.discountType.amount",
    fallback: "Amount",
  },
  // {
  //   value: "freeGiftOverAmount",
  //   labelKey: "pages.dsokCartRules.discountType.freeGiftOverAmount",
  //   fallback: "Free gift over amount",
  // },
];

function toDateLocalString(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    return `${yyyy}-${mm}-${dd}`;
  } catch (_) {
    return "";
  }
}

function toApiDateStart(dateLocalStr) {
  if (!dateLocalStr) return "";
  try {
    const d = new Date(`${dateLocalStr}T00:00:00`);
    return d.toISOString().replace("Z", "+0000");
  } catch (_) {
    return "";
  }
}

function toApiDateEnd(dateLocalStr) {
  if (!dateLocalStr) return "";
  try {
    const d = new Date(`${dateLocalStr}T23:59:59`);
    return d.toISOString().replace("Z", "+0000");
  } catch (_) {
    return "";
  }
}

const defaultInitialValues = {
  name: "",
  isActive: true,
  autoApply: false,
  order: 0,
  isCodeRequired: false,
  isOneTimeCode: false,
  discountType: "percent",
  value: 0,
  minOrderAmount: 0,
  rounding: "none",
  text: { sk: "", en: "" },
  validity: {
    dateFrom: "",
    dateTo: "",
    timeWindow: { from: "", to: "" },
    daysOfWeek: ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"],
  },
  minAmountForGift: 0,
  giftProductId: "",
  giftProductName: {},
  giftProductImage: "",
  textPositions: [],
};

const SettingsTab = ({
  initialValues,
  onSave,
  onCancel,
  onIsCodeRequiredChange,
  isAutoMode = false,
  isNew = false,
}) => {
  const { t } = useTranslation();
  const { getAuthUser } = useJumboAuth();
  const { customer } = getAuthUser();
  const effectiveLanguages = React.useMemo(() => {
    const langs = customer?.dsokSettings?.customer?.languages;
    return Array.isArray(langs) && langs.length ? langs : ["sk"];
  }, [customer]);

  const init = React.useMemo(() => {
    const merged = { ...defaultInitialValues, ...(initialValues || {}) };
    // In auto mode, set default values and ensure they cannot be changed
    if (isAutoMode) {
      merged.autoApply = true;
      merged.isCodeRequired = false;
    }
    // NEW rule screen override: new discount coupons should start as code-required
    // and user must be able to toggle one-time-code freely.
    if (isNew && !isAutoMode) {
      merged.isCodeRequired = true;
    }
    // Normalization (edit-only): if code is not required, one-time-code cannot be enabled
    if (!isNew && !merged.isCodeRequired) {
      merged.isOneTimeCode = false;
    }
    merged.validity = merged.validity || {};
    const v = merged.validity;
    merged.validity = {
      dateFrom: toDateLocalString(v.dateFrom),
      dateTo: toDateLocalString(v.dateTo),
      timeWindow: {
        from: v?.timeWindow?.from || "",
        to: v?.timeWindow?.to || "",
      },
      daysOfWeek:
        Array.isArray(v?.daysOfWeek) && v.daysOfWeek.length
          ? v.daysOfWeek
          : defaultInitialValues.validity.daysOfWeek,
    };
    merged.text = merged.text || {};
    effectiveLanguages.forEach((lng) => {
      if (merged.text[lng] === undefined) merged.text[lng] = "";
    });
    return merged;
  }, [initialValues, effectiveLanguages, isAutoMode]);

  return (
    <Formik
      enableReinitialize
      initialValues={init}
      validate={(values) => {
        const errors = {};
        if (!values.name || !String(values.name).trim()) {
          errors.name = t("validation.required", { defaultValue: "Required" });
        }
        if (values.isActive === undefined || values.isActive === null) {
          errors.isActive = t("validation.required", {
            defaultValue: "Required",
          });
        }
        if (
          !values.validity?.dateFrom ||
          !String(values.validity.dateFrom).trim()
        ) {
          errors.validity = {
            ...(errors.validity || {}),
            dateFrom: t("validation.required", { defaultValue: "Required" }),
          };
        }
        if (
          !values.validity?.dateTo ||
          !String(values.validity.dateTo).trim()
        ) {
          errors.validity = {
            ...(errors.validity || {}),
            dateTo: t("validation.required", { defaultValue: "Required" }),
          };
        }
        return errors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        try {
          const { code, ...restValues } = values || {};
          const payload = {
            ...restValues,
            rounding: values.rounding === "none" ? "none" : "mathematical",
            // Extra safety: keep combination consistent (UI-level only)
            isOneTimeCode:
              !isNew && values.isCodeRequired === false
                ? false
                : Boolean(values.isOneTimeCode),
            validity: {
              dateFrom: values.validity.dateFrom
                ? toApiDateStart(values.validity.dateFrom)
                : "",
              dateTo: values.validity.dateTo
                ? toApiDateEnd(values.validity.dateTo)
                : "",
              timeWindow: {
                from: values.validity.timeWindow?.from || "",
                to: values.validity.timeWindow?.to || "",
              },
              daysOfWeek: values.validity.daysOfWeek || [],
            },
          };
          const { customer } = getAuthUser();
          const defaultLanguage =
            customer?.dsokSettings?.customer?.defaultLanguage || "en";
          if (values.gift) {
            const nameObj =
              typeof values.gift.productName === "string"
                ? { [defaultLanguage]: values.gift.productName }
                : values.gift.productName || {};
            payload.gift = {
              id: values.gift.id || values.giftProductId || "",
              productName: nameObj,
              image: values.gift.image || values.giftProductImage || "",
            };
          } else if (values.giftProductId) {
            const nameObj =
              typeof values.giftProductName === "string"
                ? { [defaultLanguage]: values.giftProductName }
                : values.giftProductName || {};
            payload.gift = {
              id: values.giftProductId,
              productName: nameObj,
              image: values.giftProductImage || "",
            };
          }
          if (typeof onSave === "function") onSave(payload);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        values,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        errors,
        touched,
        submitCount,
      }) => (
        <Box sx={{ p: 3 }} component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={12}>
                <Alert severity="info">
                  <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    <li>
                      <Typography variant="body2">
                        {t("pages.dsokCartRules.activeHelp", {
                          defaultValue:
                            "Active: When ON, the rule applies on the kiosk if conditions match.",
                        })}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        {t("pages.dsokCartRules.autoApplyHelp", {
                          defaultValue:
                            "Auto apply: If conditions match, the rule is applied automatically for every order and its text/name is displayed at the top of the kiosk.",
                        })}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        {t("pages.dsokCartRules.isCodeRequiredHelp", {
                          defaultValue:
                            "Code required: The discount is applied only with a valid code.",
                        })}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        {t("pages.dsokCartRules.oneTimeCodesHelp", {
                          defaultValue:
                            "One time codes: Each code can be used once.",
                        })}
                      </Typography>
                    </li>
                  </Box>
                </Alert>
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(values.isActive)}
                        onChange={(e) =>
                          setFieldValue("isActive", e.target.checked)
                        }
                      />
                    }
                    label={t("common.active", { defaultValue: "Active" })}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(values.autoApply)}
                        disabled={true}
                        onChange={(e) => {
                          const next = e.target.checked;
                          setFieldValue("autoApply", next);
                        }}
                      />
                    }
                    label={t("pages.dsokCartRules.autoApply", {
                      defaultValue: "Auto apply",
                    })}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(values.isCodeRequired)}
                        disabled={true}
                        onChange={(e) => {
                          const next = e.target.checked;
                          setFieldValue("isCodeRequired", next);
                          if (typeof onIsCodeRequiredChange === "function") {
                            onIsCodeRequiredChange(next);
                          }
                        }}
                      />
                    }
                    label={t("pages.dsokCartRules.isCodeRequired")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(values.isOneTimeCode)}
                        // Route-specific behavior:
                        // - /new/auto: always disabled (auto-applied rule creation)
                        // - /new: always enabled (manual coupon creation)
                        // - edit: disabled when code is not required
                        disabled={
                          isNew && isAutoMode
                            ? true
                            : isNew
                              ? false
                              : !Boolean(values.isCodeRequired)
                        }
                        onChange={(e) =>
                          setFieldValue("isOneTimeCode", e.target.checked)
                        }
                      />
                    }
                    label={t("pages.dsokCartRules.oneTimeCodes", {
                      defaultValue: "One time codes",
                    })}
                  />
                </Stack>
                {submitCount > 0 && errors?.isActive ? (
                  <Typography variant="caption" color="error">
                    {errors.isActive}
                  </Typography>
                ) : null}
              </Grid>
              <Grid item xs={12} md={12} lg={12}></Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  name="name"
                  label={t("pages.dsokCartRules.name", {
                    defaultValue: "Name",
                  })}
                  value={values.name}
                  onChange={handleChange}
                  error={Boolean(
                    (touched?.name || submitCount > 0) && errors?.name,
                  )}
                  helperText={
                    touched?.name || submitCount > 0 ? errors?.name : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={12} lg={2}>
                <FormControl fullWidth>
                  <InputLabel id="discountType-label">
                    {t("pages.dsokCartRules.discountTypeLabel", {
                      defaultValue: "Discount Type",
                    })}
                  </InputLabel>
                  <Select
                    labelId="discountType-label"
                    name="discountType"
                    label={t("pages.dsokCartRules.discountTypeLabel", {
                      defaultValue: "Discount Type",
                    })}
                    value={values.discountType}
                    onChange={(e) =>
                      setFieldValue("discountType", e.target.value)
                    }
                  >
                    {DISCOUNT_TYPES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {t(opt.labelKey, { defaultValue: opt.fallback })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {(values.discountType === "percent" ||
                values.discountType === "amount") && (
                <Grid item xs={12} md={4} lg={2}>
                  <TextField
                    type="number"
                    fullWidth
                    name="value"
                    label={t("pages.dsokCartRules.value", {
                      defaultValue: "Value",
                    })}
                    value={values.value}
                    onChange={handleChange}
                    inputProps={{
                      step: values.discountType === "percent" ? 1 : 0.01,
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={4} lg={2}>
                <TextField
                  type="number"
                  fullWidth
                  name="minOrderAmount"
                  label={t("pages.dsokCartRules.minOrderAmount", {
                    defaultValue: "Min order amount",
                  })}
                  value={values.minOrderAmount}
                  onChange={handleChange}
                  inputProps={{ step: 0.01 }}
                />
              </Grid>
              {values.discountType === "freeGiftOverAmount" && (
                <GiftProductPicker
                  t={t}
                  value={
                    values.gift || {
                      id: values.giftProductId,
                      productName: values.giftProductName,
                      image: values.giftProductImage,
                    }
                  }
                  onChange={(gift) => {
                    setFieldValue("gift", gift);
                    setFieldValue("giftProductId", gift?.id || "");
                    setFieldValue("giftProductName", gift?.productName || {});
                    setFieldValue("giftProductImage", gift?.image || "");
                  }}
                />
              )}
              {values.autoApply && (
                <>
                  <Grid item xs={12} lg={12}>
                    <Alert severity="info">
                      {t("pages.dsokCartRules.textTitleNameHelp", {
                        defaultValue:
                          "This text will be shown on the top of the kiosk",
                      })}
                    </Alert>
                  </Grid>
                  {effectiveLanguages.map((lng) => (
                    <Grid key={lng} item xs={12} md={3} lg={3}>
                      <TextField
                        fullWidth
                        name={`textTitleName.${lng}`}
                        label={`${t("pages.dsokCartRules.textTitleName", {
                          defaultValue: "Title name",
                        })} (${String(lng).toUpperCase()})`}
                        value={values.textTitleName?.[lng] || ""}
                        onChange={(e) =>
                          setFieldValue(`textTitleName.${lng}`, e.target.value)
                        }
                        inputProps={{ maxLength: 80 }}
                      />
                    </Grid>
                  ))}
                </>
              )}
              <Grid item xs={12} lg={12}>
                <Alert severity="info">
                  {t("pages.dsokCartRules.textAppliedHelp", {
                    defaultValue:
                      "This text will be shown on the kiosk when the discount is applied",
                  })}
                </Alert>
              </Grid>
              {effectiveLanguages.map((lng) => (
                <Grid key={lng} item xs={12} md={3} lg={3}>
                  <TextField
                    fullWidth
                    name={`text.${lng}`}
                    label={`${t("pages.dsokCartRules.text", {
                      defaultValue: "Text",
                    })} (${String(lng).toUpperCase()})`}
                    value={values.text?.[lng] || ""}
                    onChange={(e) =>
                      setFieldValue(`text.${lng}`, e.target.value)
                    }
                    inputProps={{ maxLength: 80 }}
                  />
                </Grid>
              ))}
              <Grid item xs={12} lg={12}>
                <Alert severity="info">
                  {t("pages.dsokCartRules.successMessageHelp", {
                    defaultValue:
                      "This text will be shown on the bottom of the kiosk",
                  })}
                </Alert>
              </Grid>
              {effectiveLanguages.map((lng) => (
                <Grid key={lng} item xs={12} md={3} lg={3}>
                  <TextField
                    fullWidth
                    name={`successMessage.${lng}`}
                    label={`${t("pages.dsokCartRules.text", {
                      defaultValue: "Text",
                    })} (${String(lng).toUpperCase()})`}
                    value={values.successMessage?.[lng] || ""}
                    onChange={(e) =>
                      setFieldValue(`successMessage.${lng}`, e.target.value)
                    }
                    inputProps={{ maxLength: 80 }}
                  />
                </Grid>
              ))}
              <Grid item xs={12} lg={12}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t("pages.dsokCartRules.validity", {
                    defaultValue: "Validity",
                  })}
                </Typography>
                <Divider sx={{ mt: 1, mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  type="date"
                  fullWidth
                  name="validity.dateFrom"
                  label={t("pages.dsokCartRules.dateFrom", {
                    defaultValue: "Date from",
                  })}
                  value={values.validity.dateFrom}
                  onChange={(e) =>
                    setFieldValue("validity.dateFrom", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(
                    (touched?.validity?.dateFrom || submitCount > 0) &&
                    errors?.validity?.dateFrom,
                  )}
                  helperText={
                    touched?.validity?.dateFrom || submitCount > 0
                      ? errors?.validity?.dateFrom
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  type="date"
                  fullWidth
                  name="validity.dateTo"
                  label={t("pages.dsokCartRules.dateTo", {
                    defaultValue: "Date to",
                  })}
                  value={values.validity.dateTo}
                  onChange={(e) =>
                    setFieldValue("validity.dateTo", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(
                    (touched?.validity?.dateTo || submitCount > 0) &&
                    errors?.validity?.dateTo,
                  )}
                  helperText={
                    touched?.validity?.dateTo || submitCount > 0
                      ? errors?.validity?.dateTo
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  type="time"
                  fullWidth
                  name="validity.timeWindow.from"
                  label={t("pages.dsokCartRules.timeFrom", {
                    defaultValue: "Time from",
                  })}
                  value={values.validity.timeWindow?.from || ""}
                  onChange={(e) =>
                    setFieldValue("validity.timeWindow.from", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  type="time"
                  fullWidth
                  name="validity.timeWindow.to"
                  label={t("pages.dsokCartRules.timeTo", {
                    defaultValue: "Time to",
                  })}
                  value={values.validity.timeWindow?.to || ""}
                  onChange={(e) =>
                    setFieldValue("validity.timeWindow.to", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  {t("pages.dsokCartRules.daysOfWeek", {
                    defaultValue: "Days of week",
                  })}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {DAYS_OF_WEEK.map((d) => {
                    const selected = (
                      values.validity.daysOfWeek || []
                    ).includes(d.key);
                    const toggle = () => {
                      const current = new Set(values.validity.daysOfWeek || []);
                      if (current.has(d.key)) current.delete(d.key);
                      else current.add(d.key);
                      const order = DAYS_OF_WEEK.map((x) => x.key);
                      const next = Array.from(current).sort(
                        (a, b) => order.indexOf(a) - order.indexOf(b),
                      );
                      setFieldValue("validity.daysOfWeek", next);
                    };
                    return (
                      <FormControlLabel
                        key={d.key}
                        control={
                          <Switch checked={selected} onChange={toggle} />
                        }
                        label={t(d.labelKey, { defaultValue: d.fallback })}
                      />
                    );
                  })}
                </Box>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              {onCancel && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  {t("buttons.cancel", { defaultValue: "Cancel" })}
                </Button>
              )}
              <Button variant="contained" type="submit" disabled={isSubmitting}>
                {t("buttons.save", { defaultValue: "Save" })}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </Formik>
  );
};

export default SettingsTab;
